import { orgSelectors } from 'ducks/orgs'
import { Typography } from 'opensolar-ui'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { StudioSystemType } from 'types/global'
import { MOUNTING_SYSTEMS, MountingID, MountingSystemType, MountingSystemTypeOptions } from 'types/mounting'
import { clear as clearMountingSystem } from '../nativeMounting/calculate'
import MountingSystemSelector from './MountingSystemSelector'
import MountingSystemTypeSelector from './MountingSystemTypeSelector'
import RoofTypeSelector, { shouldDisableRoofType } from './RoofTypeSelector'
import useApplyMountingSystem from './useApplyMountingSystem'

export const allMountingTypeOptions: MountingSystemTypeOptions[] = [
  {
    type: 'onRoof',
    label: 'On-Roof',
    mountingIds: Object.entries(MOUNTING_SYSTEMS)
      .filter(([_, data]) => data.types.includes('onRoof'))
      .map(([id, _]) => id as MountingID),
  },
  {
    type: 'inRoof',
    label: 'In-Roof',
    mountingIds: Object.entries(MOUNTING_SYSTEMS)
      .filter(([_, data]) => data.types.includes('inRoof'))
      .map(([id, _]) => id as MountingID),
  },
  {
    type: 'flatRoof',
    label: 'Flat-Roof',
    mountingIds: Object.entries(MOUNTING_SYSTEMS)
      .filter(([_, data]) => data.types.includes('flatRoof'))
      .map(([id, _]) => id as MountingID),
  },
  {
    type: 'ground',
    label: 'Ground',
    mountingIds: Object.entries(MOUNTING_SYSTEMS)
      .filter(([_, data]) => data.types.includes('ground'))
      .map(([id, _]) => id as MountingID),
  },
]

export const mountingIdAvailabilityByIso2: Record<MountingID, string[]> = Object.fromEntries(
  Object.entries(MOUNTING_SYSTEMS).map(([id, data]) => [id, data.availableCountries])
) as Record<MountingID, string[]>

export const mountingToManufacturereMap: Record<MountingID, string> = Object.fromEntries(
  Object.entries(MOUNTING_SYSTEMS).map(([id, data]) => [id, data.manufacturer])
) as Record<MountingID, string>

export const useGetMountingOptsInCountry = (): MountingSystemTypeOptions[] => {
  const countryIso2 = useSelector(orgSelectors.getOrgIso2) || ''
  return useMemo(() => {
    return allMountingTypeOptions.reduce(
      (acc: MountingSystemTypeOptions[], mountingOption: MountingSystemTypeOptions) => {
        const availableSuppliersInCountry = mountingOption.mountingIds.filter((supplier) =>
          mountingIdAvailabilityByIso2[supplier].includes(countryIso2)
        )
        if (availableSuppliersInCountry.length === 0) {
          return acc
        } else {
          acc.push({ ...mountingOption, mountingIds: availableSuppliersInCountry })
          return acc
        }
      },
      []
    )
  }, [countryIso2])
}

const useSelectedMountingSystemType = ({
  systemMountingType,
  systemMountingSystem,
  mountingOpts,
}): MountingSystemType => {
  return useMemo(() => {
    if (systemMountingType) {
      return systemMountingType
    } else if (systemMountingSystem !== undefined && systemMountingType === undefined) {
      // Inferred mounting type from selected mounting system
      const inferred = mountingOpts.find((option) => option.mountingIds.includes(systemMountingSystem))
      return inferred?.type
    } else if (systemMountingSystem === undefined && systemMountingType === undefined) {
      const onRoofOption = mountingOpts.find((option) => option.type === 'onRoof')
      return onRoofOption?.type ?? mountingOpts[0]?.type
    }
  }, [systemMountingType, systemMountingSystem, mountingOpts])
}

const MountingSelector = ({ system, allowEdit }: { system: StudioSystemType; allowEdit: boolean }) => {
  const translate = useTranslate()
  const mountingTypeOptions = useGetMountingOptsInCountry()

  const applyMountingSystem = useApplyMountingSystem()
  const selectedMountingSystem = system.mounting
  const selectedType = useSelectedMountingSystemType({
    systemMountingType: system.mounting_type,
    systemMountingSystem: selectedMountingSystem,
    mountingOpts: mountingTypeOptions,
  })
  const handleSelectMountingType = useCallback((value: MountingSystemType): void => {
    const system = window.editor.selectedSystem
    const commandUUID = window.Utils.generateCommandUUIDOrUseGlobal()
    window.editor.execute(new window.SetValueCommand(system, 'mounting_type', value, commandUUID))
    window.editor.signals.objectChanged.dispatch(system, 'mounting_type')
  }, [])

  const handleUpdateMountingSystem = (mountingId: MountingID | undefined) => {
    applyMountingSystem({ mountingId, system, mountingType: selectedType })

    if (!mountingId) clearMountingSystem(system)
  }
  const isRoofTypeSelectorDisabled = shouldDisableRoofType(selectedMountingSystem)

  // Persist inferred type for systems saved with mounting but no mounting_type (legacy).
  useEffect(() => {
    if (!system.mounting || system.mounting_type || !selectedType) {
      return
    }
    const commandUUID = window.Utils.generateCommandUUIDOrUseGlobal()
    window.editor.execute(new window.SetValueCommand(system, 'mounting_type', selectedType, commandUUID))
    window.editor.signals.objectChanged.dispatch(system, 'mounting_type')
  }, [system, system.mounting, system.mounting_type, selectedType])

  return (
    <div>
      <Typography variant="subtitle2" gutterBottom>
        {translate('Mounting System Type')}
      </Typography>
      <MountingSystemTypeSelector
        disabled={!!selectedMountingSystem || !allowEdit}
        selectedType={selectedType}
        handleSelectMountingType={handleSelectMountingType}
      />
      {selectedType !== 'ground' && <RoofTypeSelector />}
      <MountingSystemSelector
        selectedType={selectedType}
        selectedMountingSystem={selectedMountingSystem}
        handleUpdateMountingSystem={handleUpdateMountingSystem}
        isRoofTypeSelectorDisabled={isRoofTypeSelectorDisabled}
      />
    </div>
  )
}

export default MountingSelector
